/**
 * Created by Ning Jiang on 5/9/2019.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {NudgeStackedSymbolWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/nudge/NudgeStackedSymbolWPController";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {TimelineLite} from "gsap";
import {PixiesVsPiratesGameConfig} from "../../PixiesVsPiratesGameConfig";
import {PixiesVsPiratesSoundConfig} from "../../PixiesVsPiratesSoundConfig";
import {PPReelController} from "../../reel/PPReelController";
import {PixiesVsPiratesParsedGameData} from "../../server/parseddata/PixiesVsPiratesParsedGameData";
import {PixiesVsPiratesParsedInitData} from "../../server/parseddata/PixiesVsPiratesParsedInitData";
import {PPWildSymbol} from "../../symbols/PPWildSymbol";

export class PPNudgeWildWPController extends NudgeStackedSymbolWPController {

    private _writeOnTimelines:TimelineLite[];
    private _nudgeCompleted:boolean = true;
    private _winOnReel4:boolean = false;
    private _writeOnStates:boolean[];
    private _actualReelSize:number[];

    private _wildMultipliers:number[];
    private _restoreMode:GameMode;

    constructor(indexes:[number, number]) {
        super(indexes, {nudgeStepSpeed : 0.6, stackedSymbolKeyword : "W"});
    }

    protected addFeatureEventHandlers():void {
        super.addFeatureEventHandlers();
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ScreenEvent.GAME_RESTORE, (event:GameEvent) => this.onGameRestore());
    }

    private onInitDataParsed(data:PixiesVsPiratesParsedInitData):void {
        this._restoreMode = data.mode;
        this._wildMultipliers = data.wildMultipliers.concat();
    }

    private onGameRestore():void {
        if(this._restoreMode != GameMode.FREESPIN) {
            return;
        }

        this._wildMultipliers.forEach((multiplier:number, reelId:number) => {
            if(multiplier > 0) {
                const symbol:PPWildSymbol = <PPWildSymbol>Reels.getSymbol(reelId, 5);
                if(!(symbol instanceof PPWildSymbol)) {
                    Logger.logDev(`PPNudgeWildWPController.onGameRestore():Cannot find wild on reel ${reelId}`);
                    debugger;
                }
                symbol.updateMultiplier(multiplier, false);
            }
        });
    }

    protected parseFeatureGameData(data:PixiesVsPiratesParsedGameData):void {
        super.parseFeatureGameData(data);
        this._actualReelSize = data.gameAreaSize.concat();
        this._winOnReel4 = (<IBetWayWinData[]>data.betWins).some((betWayWinData:IBetWayWinData) => {
            return betWayWinData.length > 4;
        });
    }

    protected startWinPresentation():void {
        this._nudgeCompleted = false;
        super.startWinPresentation();
    }

    protected getNudgeRange(reelId:number):[number, number] {
        const rangeBottom:number = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];
        return [rangeBottom - this._actualReelSize[reelId], rangeBottom];
    }

    protected getReelNoWinNudge(reelId:number):boolean {
        return this._noWinNudge || (reelId === 4 && !this._winOnReel4);
    }

    protected playNudgeStepStart(reelId:number, stepsLeft:number):void {
        this.playNudgeSounds(reelId, stepsLeft);
        const symbol:PPWildSymbol = <PPWildSymbol>Reels.getSymbol(reelId, this._nudgeSymbolId[reelId]);
        if(symbol) {
            symbol.changeState({
                state : PixiesVsPiratesGameConfig.instance.SYMBOL_STATES.nudge,
                // setStack : true,
                fadeInDuration : 0.5
            });
        }
    }

    private playNudgeSounds(reelId:number, stepsLeft:number) {
        switch(true) {
            case ((this._steps[reelId] > 1 || this._steps[reelId] < -1) && stepsLeft === this._steps[reelId]):
                SlotGame.sound.playEffect(PixiesVsPiratesSoundConfig.NUDGE_START);
                break;
            case ((this._steps[reelId] === 1 || this._steps[reelId] === -1) && stepsLeft === this._steps[reelId]):
                SlotGame.sound.playEffect(PixiesVsPiratesSoundConfig.NUDGE_ONE_TIME);
                break;
            case ((this._steps[reelId] > 1 || this._steps[reelId] < -1) && (stepsLeft === 1 || stepsLeft === -1)):
                SlotGame.sound.playEffect(PixiesVsPiratesSoundConfig.NUDGE_END);
                break;
            case ((this._steps[reelId] > 1 || this._steps[reelId] < -1) && stepsLeft !== this._steps[reelId]):
                SlotGame.sound.playEffect(stepsLeft % 2 === 0 ? PixiesVsPiratesSoundConfig.NUDGE_EVEN : PixiesVsPiratesSoundConfig.NUDGE_ODD);
                break;
        }
    }

    protected playNudgeAbortSound():void {
        SlotGame.sound.playEffect(PixiesVsPiratesSoundConfig.NUDGE_END);
    }

    protected playOnNudgeStepComplete(reelId:number, stepsLeft:number):void {
        if(this.getReelNoWinNudge(reelId)) {
            //Add a bigger bounce for the last step in no win nudge.
            if(Math.abs(stepsLeft) === 1) {
                SlotGame.sound.playEffect(PixiesVsPiratesSoundConfig.NUDGE_NO_WIN);
                (<PPReelController>Reels.getReel(reelId)).updateNoWinNudgeSpeed();
            }
        } else {
            const symbol:PPWildSymbol = <PPWildSymbol>Reels.getSymbol(reelId, this._nudgeSymbolId[reelId]);
            if(symbol) {
                symbol.playNudgeSparkles();
                symbol.updateMultiplier(Math.abs(this._nudgedSteps[reelId]) + 1, true);
            }
        }
    }

    protected playOnReelNudgeComplete(reelId:number, aborted:boolean):void {
        const symbol:PPWildSymbol = <PPWildSymbol>Reels.getSymbol(reelId, this._nudgeSymbolId[reelId]);
        if(symbol) {
            if(this.getReelNoWinNudge(reelId)) {
                (<PPWildSymbol[]>symbol.stack!.symbols).forEach((symbol:PPWildSymbol) => {
                    symbol.updateMultiplier(Math.abs(this._nudgedSteps[reelId]) + 1, false);
                });
            } else {
                if(this._steps[reelId] === 0) {
                    SlotGame.sound.playEffect(PixiesVsPiratesSoundConfig.NUDGE_END_ANIMATION);
                }
                const timeline:TimelineLite = new TimelineLite();
                timeline.add(symbol.playWriteOn(true));
                timeline.add(() => {
                    this._writeOnStates[reelId] = false;
                    this.checkComplete();
                });
                timeline.timeScale(aborted? 20 : 1);
                this._writeOnTimelines.push(timeline);
                this._writeOnStates[reelId] = true;
            }
        }
    }

    protected playOnAllNudgeComplete():void {
        this._nudgeCompleted = true;
        this.checkComplete();
    }

    protected checkComplete():void {
        if(!this._nudgeCompleted || this._writeOnStates.some(state => state)) {
            return;
        }

        this.finish();
    }

    protected stopWinPresentation(isAborted:boolean):void {
        super.stopWinPresentation(isAborted);

        // EventHandler.dispatchEvent(new GameEvent(TombstoneMultiplierController.SHOW_END_MULTIPLIER, isAborted && !this._noWinNudge));

        // This is the first wp, so the startWP will always be triggered, no need to think about abort before start.
    }

    public abort():void {
        if(this._isPlaying && this._skippable) {
            super.abort();

            this._writeOnTimelines.forEach((timeline:TimelineLite) => {
                if(timeline && timeline.isActive()) {
                    timeline.timeScale(20);
                }
            });
        }
    }

    protected abortNudgingReel(reelId:number):void {
        super.abortNudgingReel(reelId);

        const symbol:PPWildSymbol = <PPWildSymbol>Reels.getSymbol(reelId, this._nudgeSymbolId[reelId]);
        if(symbol) {
            if(this.getReelNoWinNudge(reelId)) {
                symbol.updateMultiplier(Math.abs(this._steps[reelId]) + 1, false);
            } else {
                symbol.playNudgeSparkles();
                symbol.updateMultiplier(Math.abs(this._steps[reelId]) + 1, true);
            }
        }
    }

    protected resetFeature():void {
        super.resetFeature();
        this._writeOnTimelines = [];
        this._winOnReel4 = false;
        this._writeOnStates = ArrayHelper.initArrayWithValues<boolean>(GameConfig.instance.REELS_NUM, () => false);
    }
}